<template><div><code-well-header>
  <dt-input-group
    name="fruits-input-group"
    legend="Fruits"
  >
    <dt-radio value="apple"><span>Apple</span></dt-radio>
    <dt-radio value="banana"><span>Banana</span></dt-radio>
    <dt-radio value="other"><span>Other</span></dt-radio>
  </dt-input-group>
</code-well-header>
<h2 id="default" tabindex="-1" class="d-docsite--header-2">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Input Groups are typically paired with a legend which identifies the group. If no legend is provided then it is expected that an <code v-pre>aria-label</code> will be given in order to provide an invisible label to screen readers. Each Input Group should contain one or more inputs which users can interact with.</p>
<code-well-header>
  <dt-input-group
    ref="baseExample"
    name="fruits-input-group"
  >
    <dt-radio value="apple"><span>Apple</span></dt-radio>
    <dt-radio value="banana"><span>Banana</span></dt-radio>
    <dt-radio value="other"><span>Other</span></dt-radio>
  </dt-input-group>
</code-well-header>
<code-example-tabs
:htmlCode='() => $refs.baseExample'
vueCode='
<dt-input-group
  name="fruits-input-group"
>
  <dt-radio value="apple"><span>Apple</span></dt-radio>
  <dt-radio value="banana"><span>Banana</span></dt-radio>
  <dt-radio value="other"><span>Other</span></dt-radio>
</dt-input-group>
'
/>
<h2 id="model" tabindex="-1" class="d-docsite--header-2">Model <a class="header-anchor d-link d-docsite--link d-link" href="#model" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The Vue model can have one of the following types <code v-pre>[String, Number, Boolean, Object]</code>:</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token comment">// default = null</span></span>
<span class="line"><span class="token keyword">const</span> value <span class="token operator">=</span> <span class="token string">'some value'</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">The Vue model is dependant on the child component(s) implementing the provided <code v-pre>groupContext</code> and calling the provided <code v-pre>setGroupValue</code> method which will handle updating the provided <code v-pre>groupContext</code> and Vue model in the parent.</p>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span></span>
<span class="line">  DtInputMixin<span class="token punctuation">,</span></span>
<span class="line">  DtGroupableInputMixin<span class="token punctuation">,</span></span>
<span class="line"><span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'@dialpad/dialtone/vue3'</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">export</span> <span class="token keyword">default</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token literal-property property">name</span><span class="token operator">:</span> <span class="token string">'MyInputElement'</span><span class="token punctuation">,</span></span>
<span class="line"></span>
<span class="line">  <span class="token literal-property property">mixins</span><span class="token operator">:</span> <span class="token punctuation">[</span>DtInputMixin<span class="token punctuation">,</span> DtGroupableInputMixin<span class="token punctuation">]</span><span class="token punctuation">,</span></span>
<span class="line"></span>
<span class="line">  <span class="token literal-property property">computed</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token function">groupValue</span> <span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">      <span class="token keyword">return</span> <span class="token keyword">this</span><span class="token punctuation">.</span>groupContext<span class="token operator">?.</span>value<span class="token punctuation">;</span></span>
<span class="line">    <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"></span>
<span class="line">    <span class="token function">inputListeners</span> <span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">      <span class="token keyword">return</span> <span class="token punctuation">{</span></span>
<span class="line">        <span class="token function-variable function">change</span><span class="token operator">:</span> <span class="token parameter">event</span> <span class="token operator">=></span> <span class="token keyword">this</span><span class="token punctuation">.</span><span class="token function">emitValue</span><span class="token punctuation">(</span>event<span class="token punctuation">.</span>target<span class="token punctuation">.</span>value<span class="token punctuation">)</span><span class="token punctuation">,</span></span>
<span class="line">      <span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line">    <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"></span>
<span class="line">  <span class="token literal-property property">watch</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token literal-property property">groupValue</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">      <span class="token literal-property property">immediate</span><span class="token operator">:</span> <span class="token boolean">true</span><span class="token punctuation">,</span></span>
<span class="line">      <span class="token function">handler</span> <span class="token punctuation">(</span><span class="token parameter">newGroupValue</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">        <span class="token keyword">if</span> <span class="token punctuation">(</span><span class="token keyword">this</span><span class="token punctuation">.</span>hasGroup<span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">          <span class="token comment">// update internal value when the input group value changes</span></span>
<span class="line">          <span class="token keyword">this</span><span class="token punctuation">.</span>internalChecked <span class="token operator">=</span> newGroupValue <span class="token operator">===</span> <span class="token keyword">this</span><span class="token punctuation">.</span>value<span class="token punctuation">;</span></span>
<span class="line">        <span class="token punctuation">}</span></span>
<span class="line">      <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"></span>
<span class="line">  <span class="token literal-property property">methods</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token function">emitValue</span> <span class="token punctuation">(</span><span class="token parameter">value</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">      <span class="token keyword">if</span> <span class="token punctuation">(</span>value <span class="token operator">!==</span> <span class="token keyword">this</span><span class="token punctuation">.</span>groupValue<span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">        <span class="token comment">// update provided value if injected</span></span>
<span class="line">        <span class="token keyword">this</span><span class="token punctuation">.</span><span class="token function">setGroupValue</span><span class="token punctuation">(</span>value<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line">        <span class="token keyword">this</span><span class="token punctuation">.</span><span class="token function">$emit</span><span class="token punctuation">(</span><span class="token string">'input'</span><span class="token punctuation">,</span> value<span class="token punctuation">)</span><span class="token punctuation">;</span></span>
<span class="line">      <span class="token punctuation">}</span></span>
<span class="line">    <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="with-legend" tabindex="-1" class="d-docsite--header-3">With Legend <a class="header-anchor d-link d-docsite--link d-link" href="#with-legend" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-input-group
    name="input-group-with-legend"
    legend="With Legend"
  >
    <dt-radio value="apple"><span>Apple</span></dt-radio>
    <dt-radio value="banana"><span>Banana</span></dt-radio>
    <dt-radio value="other"><span>Other</span></dt-radio>
  </dt-input-group>
</code-well-header>
<code-example-tabs
vueCode='
<dt-input-group
  name="input-group-with-legend"
  legend="With Legend"
>
  <!-- Input Elements -->
</dt-input-group>
'
/>
<h3 id="with-slotted-legend" tabindex="-1" class="d-docsite--header-3">With Slotted Legend <a class="header-anchor d-link d-docsite--link d-link" href="#with-slotted-legend" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-input-group name="input-group-with-slotted-legend">
    <dt-radio value="apple"><span>Apple</span></dt-radio>
    <dt-radio value="banana"><span>Banana</span></dt-radio>
    <dt-radio value="other"><span>Other</span></dt-radio>
    <template #legend>
      With Slotted Legend
    </template>
  </dt-input-group>
</code-well-header>
<code-example-tabs
vueCode='
<dt-input-group
  name="input-group-with-legend"
>
  <!-- Input Elements -->
  <template #legend>
    With Slotted Legend
  </template>
</dt-input-group>
'
/>
<h3 id="disabled" tabindex="-1" class="d-docsite--header-3">Disabled <a class="header-anchor d-link d-docsite--link d-link" href="#disabled" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-input-group
    name="input-group-disabled"
    legend="Disabled"
    disabled
  >
    <dt-radio value="apple"><span>Apple</span></dt-radio>
    <dt-radio value="banana"><span>Banana</span></dt-radio>
    <dt-radio value="other"><span>Other</span></dt-radio>
  </dt-input-group>
</code-well-header>
<code-example-tabs
vueCode='
<dt-input-group
  name="input-group-disabled"
  legend="Disabled"
  disabled
>
  <!-- Input Elements -->
</dt-input-group>
'
/>
<h3 id="with-validation-messages" tabindex="-1" class="d-docsite--header-3">With Validation Messages <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-messages" aria-hidden="true">#</a></h3>
<code-well-header>
  <div class="d-stack16">
    <dt-input-group
      name="input-group-with-success-message"
      legend="With Success Message"
      :messages="[{ message: 'Success validation message', type: 'success' }]"
    >
      <dt-radio value="apple"><span>Apple</span></dt-radio>
      <dt-radio value="banana"><span>Banana</span></dt-radio>
      <dt-radio value="other"><span>Other</span></dt-radio>
    </dt-input-group>
    <dt-input-group
      name="input-group-with-warning-message"
      legend="With Warning Message"
      :messages="[{ message: 'Warning', type: 'warning' }]"
    >
      <dt-radio value="apple"><span>Apple</span></dt-radio>
      <dt-radio value="banana"><span>Banana</span></dt-radio>
      <dt-radio value="other"><span>Other</span></dt-radio>
    </dt-input-group>
    <dt-input-group
      name="input-group-with-error-message"
      legend="With Error Message"
      :messages="[{ message: 'Error', type: 'error' }]"
    >
      <dt-radio value="apple"><span>Apple</span></dt-radio>
      <dt-radio value="banana"><span>Banana</span></dt-radio>
      <dt-radio value="other"><span>Other</span></dt-radio>
    </dt-input-group>
  </div>
</code-well-header>
<code-example-tabs
vueCode='
<dt-input-group
  name="input-group-with-success-message"
  legend="With Success Message"
  :messages="[{ message: `Success validation message`, type: VALIDATION_MESSAGE_TYPES.SUCCESS }]"
>
  <!-- Input Elements -->
</dt-input-group>
<dt-input-group
  name="input-group-with-warning-message"
  legend="With Warning Message"
  :messages="[{ message: `Warning`, type: VALIDATION_MESSAGE_TYPES.WARNING }]"
>
  <!-- Input Elements -->
</dt-input-group>
<dt-input-group
  name="input-group-with-error-message"
  legend="With Error Message"
  :messages="[{ message: `Error`, type: VALIDATION_MESSAGE_TYPES.ERROR }]"
>
  <!-- Input Elements -->
</dt-input-group>
'
/>
<h3 id="with-validation-messages-hidden" tabindex="-1" class="d-docsite--header-3">With Validation Messages Hidden <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-messages-hidden" aria-hidden="true">#</a></h3>
<code-well-header>
  <dt-input-group
    name="input-group-with-error-messages-hidden"
    legend="With Error Messages Hidden"
    :messages="[{ message: 'Error', type: 'error' }]"
    :show-messages="false"
  >
    <dt-radio value="apple"><span>Apple</span></dt-radio>
    <dt-radio value="banana"><span>Banana</span></dt-radio>
    <dt-radio value="other"><span>Other</span></dt-radio>
  </dt-input-group>
</code-well-header>
<code-example-tabs
vueCode='
<dt-input-group
  name="input-group-with-error-messages-hidden"
  legend="With Error Messages Hidden"
  :messages="[{ message: `Error`, type: VALIDATION_MESSAGE_TYPES.ERROR }]"
  :show-messages="false"
>
  <!-- Input Elements -->
</dt-input-group>
'
/>
<h2 id="extending" tabindex="-1" class="d-docsite--header-2">Extending <a class="header-anchor d-link d-docsite--link d-link" href="#extending" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">If your input(s) require additional logic in order to be grouped then you can extend the Input Group using <a href="https://vuejs.org/api/options-composition.html#extends" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">extends</a> in your Vue SFC.</p>
<h3 id="example" tabindex="-1" class="d-docsite--header-3">Example <a class="header-anchor d-link d-docsite--link d-link" href="#example" aria-hidden="true">#</a></h3>
<div class="language-vue" data-highlighter="prismjs" data-ext="vue"><pre v-pre><code class="language-vue"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>script</span><span class="token punctuation">></span></span><span class="token script"><span class="token language-javascript"></span>
<span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtInputGroup <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'@dialpad/dialtone/vue3'</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">export</span> <span class="token keyword">default</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token literal-property property">name</span><span class="token operator">:</span> <span class="token string">"MyComponent"</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token keyword">extends</span><span class="token operator">:</span> DtInputGroup<span class="token punctuation">,</span></span>
<span class="line">  <span class="token operator">...</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>script</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="inputgroup" /></div></template>


